/**
 * Discharge letter PDF — renders the printable discharge sheet from
 * a template config. Covers neutering and surgery discharge (and future
 * similar discharge types).
 */
import { Document, Page, StyleSheet, Text, View } from '@react-pdf/renderer'

import { ClinicHeader } from '@/frontend/features/printing/documents/blocks/clinic-header'
import {
  FillField,
  Spacer,
  WithInlineResponse,
} from '@/frontend/features/printing/documents/blocks/pdf-fields'
import { baseStyles } from '@/frontend/features/printing/documents/blocks/pdf-styles'
import type {
  DischargeLetterConfig,
  DischargeLetterFields,
} from '@/frontend/features/printing/documents/discharge/discharge-template.types'
import {
  activityText,
  anaesthesiaText,
  BATHING_TEXT,
  closingText,
  FOLLOW_UP_WHATSAPP,
  foodWaterText,
  MEDICATION_SIDE_EFFECTS,
  medicationText,
  monitoringText,
  OUT_OF_HOURS,
  painMedIntroText,
  THINGS_TO_NOTICE,
  woundPhotoText,
} from '@/frontend/features/printing/documents/discharge/shared'
import type { DocumentContext } from '@/frontend/features/printing/documents/types'
import { mm } from '@/frontend/features/printing/pdf-utils'

const styles = StyleSheet.create({
  ...baseStyles,
  sectionTitle: {
    fontFamily: 'Helvetica-Bold',
    fontSize: 10,
    textDecoration: 'underline',
    marginTop: mm(5),
    marginBottom: mm(2),
  },
})

export function DischargeLetterPdf({
  config,
  context,
  fields,
}: {
  config: DischargeLetterConfig
  context: DocumentContext
  fields: DischargeLetterFields
}) {
  const { patient, clinic } = context
  const name = patient.name

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <ClinicHeader clinic={clinic} />

        {config.title && (
          <Text style={{ ...styles.bold, fontSize: 11, marginBottom: mm(4) }}>{config.title}</Text>
        )}

        {/* Procedure line — fixed text or text + fill-in field */}
        {config.procedureField ? (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'baseline',
              gap: mm(2),
              flexWrap: 'wrap',
              marginBottom: mm(4),
            }}
          >
            <Text style={styles.bold}>Procedure:</Text>
            <Text style={styles.text}>{config.procedureText(name)}</Text>
            <FillField value={fields.procedureName || undefined} />
          </View>
        ) : (
          <Text style={styles.text}>
            <Text style={styles.bold}>Procedure: </Text>
            {config.procedureText(name)}
          </Text>
        )}

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            <Text style={styles.bold}>Activity: </Text>
            {activityText(name)}
          </Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            <Text style={styles.bold}>Anaesthesia: </Text>
            {anaesthesiaText(name)}
          </Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            <Text style={styles.bold}>Food/Water: </Text>
            {foodWaterText(name)}
          </Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            <Text style={styles.bold}>Bathing: </Text>
            {BATHING_TEXT}
          </Text>
        </View>

        <View style={styles.sectionGap}>
          <Text style={styles.text}>
            <Text style={styles.bold}>Monitoring: </Text>
            {monitoringText(name)}
          </Text>
        </View>

        <Text style={styles.sectionTitle}>Medication:</Text>
        <Text style={styles.text}>{medicationText(name)}</Text>
        <Spacer height={mm(2)} />
        <WithInlineResponse response={<FillField value={fields.painMedication || undefined} />}>
          <Text style={styles.text}>{painMedIntroText(name)}</Text>
        </WithInlineResponse>
        <Spacer height={mm(2)} />
        <Text style={styles.text}>{MEDICATION_SIDE_EFFECTS}</Text>

        <Text style={styles.sectionTitle}>Follow up appointments:</Text>
        <Text style={styles.listItem}>• {FOLLOW_UP_WHATSAPP}</Text>
        <Text style={styles.listItem}>• {woundPhotoText(name)}</Text>
        <View
          style={{ flexDirection: 'row', alignItems: 'center', gap: mm(3), paddingLeft: mm(10) }}
        >
          <FillField value={fields.woundCheckDate1 || undefined} />
          <Text style={{ fontSize: 10 }}>&</Text>
          <FillField value={fields.woundCheckDate2 || undefined} />
        </View>

        <Text style={styles.sectionTitle}>Other things you may notice:</Text>
        {THINGS_TO_NOTICE.map((item) => (
          <Text key={item.slice(0, 40)} style={styles.listItem}>
            • {item}
          </Text>
        ))}

        <Text style={styles.sectionTitle}>Out of hours contact:</Text>
        <Text style={styles.text}>{OUT_OF_HOURS}</Text>

        <Spacer height={mm(4)} />
        <Text style={styles.text}>{closingText(name)}</Text>
        <Text style={styles.text}>Snoots Vet, North London</Text>
      </Page>
    </Document>
  )
}
